/**
 * fetchWithTimeout — a drop-in `fetch` that aborts after `ms` (default 25s).
 *
 * The RAG/summarize backend runs on a single worker and can be blocked by long
 * synchronous work; while that happens it accepts the TCP connection but never
 * sends a response, so a plain `fetch()` hangs indefinitely and the calling UI
 * spins forever. Bounding every backend call converts that hang into a normal
 * rejection the caller's existing `.catch()` can surface as an error/retry.
 *
 * Pass your own `signal` and it is honored alongside the timeout (whichever
 * fires first wins). On timeout the thrown error's `name` is `'AbortError'`.
 */
export async function fetchWithTimeout(
  input: RequestInfo | URL,
  init: RequestInit = {},
  ms = 25000,
): Promise<Response> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), ms);

  // If the caller passed a signal, abort our controller when theirs fires.
  const external = init.signal;
  if (external) {
    if (external.aborted) controller.abort();
    else external.addEventListener('abort', () => controller.abort(), { once: true });
  }

  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timeout);
  }
}
